import { useEffect, useState } from 'react';
import { Button, Empty, Table, TableProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

// services
import NotificationService from 'core/services/notification';
import { onDeleteCurrency, onGetCurrencies } from 'modules/currencies/services/currencies';

// models
import { Currency } from 'modules/currencies/models/currencies';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import Icon from 'utils/icon';
import AuthActionComponent from 'components/authAction/authAction';
import NoData from 'components/noData/noData';
import { useTranslation } from 'react-i18next';
import { isRTL } from 'core/services/language';

function ListCurrenciesPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [currencies, setCurrencies] = useState<Currency[]>([]);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [tempDeletedCurrency, setTempDeletedCurrency] = useState<number>();

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps<Currency>['columns'] = [
		{
			title: t('CURRENCIES.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name, currency) => (
				<p className="mb-0 fw-medium">
					{isRTL() ? name.ar : name.en} {currency.default && <span className="status default ms-1">Default</span>}
				</p>
			),
		},
		{
			title: t('CURRENCIES.CODE'),
			dataIndex: 'code',
			key: 'code',
			render: (code) => <p className="mb-0 fw-medium">{code}</p>,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'active',
			key: 'active',
			render: (active) => (
				<span className={`status fw-medium ${active ? 'active' : 'inactive'}`}>{active ? t('COMMON.ACTIVE') : t('COMMON.INACTIVE')}</span>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, currency) => (
				<div className="d-flex gap-2">
					<AuthActionComponent action="currency_update">
						<Link to={`${currency.id}`}>
							<Icon name="edit" width={35} height={35} />
						</Link>
					</AuthActionComponent>
					<AuthActionComponent action="currency_delete">
						<Button type="link" className="p-0" onClick={() => onShowConfirmation(currency.id)}>
							<Icon name="delete" width={35} height={35} />
						</Button>
					</AuthActionComponent>
				</div>
			),
		},
	];

	useEffect(() => {
		getCurrencies();
	}, []);

	const getCurrencies = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCurrencies();
			setCurrencies(response.data.data.currencies);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetCurrencies(page);
			setCurrencies(response.data.data.currencies);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			setIsLoadMore(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const onShowConfirmation = (currencyId: number) => {
		setShowConfirmation(true);
		setTempDeletedCurrency(currencyId);
	};

	const onDiscardConfirmation = () => {
		setShowConfirmation(false);
	};

	const onDelete = async () => {
		try {
			setIsDeleting(true);
			await onDeleteCurrency(tempDeletedCurrency as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('CURRENCIES.DELETED_SUCCESS'));
			setShowConfirmation(false);
			setIsDeleting(false);
			getCurrencies();
		} catch (error) {
			setIsDeleting(false);
			setShowConfirmation(false);
			NotificationService.error(t('COMMON.OPS'), t('CURRENCIES.DELETE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('CURRENCIES.MANAGEMENT_TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className={`sectionContainer listData`}>
					<header>
						<div>
							<h5>
								{totalRecords} {t('CURRENCIES.MANAGEMENT_COUNT')}
							</h5>
							<p>{t('CURRENCIES.MANAGEMENT_DESCRIPTION')}</p>
						</div>
						<AuthActionComponent action="currency_create">
							<Link to="create">
								<Button type="primary" icon={<PlusOutlined />} style={{ background: '#F1F1F2', color: '#7E8299', border: 'none' }}>
									{t('COMMON.ADD')}
								</Button>
							</Link>
						</AuthActionComponent>
					</header>

					<div style={{ position: 'relative' }}>
						<Table
							columns={columns}
							dataSource={currencies}
							scroll={{ x: 600 }}
							rowKey="id"
							pagination={{
								pageSize: 15,
								total: totalRecords,
								showSizeChanger: false,
								current: currentPage,
								onChange: fetchMoreData,
							}}
							locale={{ emptyText: <NoData /> }}
						/>

						{isLoadMore && (
							<div className="loadMoreContainer">
								<LoadingComponent />
							</div>
						)}
					</div>
				</div>
			)}

			<ConfirmModalComponent
				open={showConfirmation}
				confirmText={t('COMMON.DELETE')}
				onDiscard={onDiscardConfirmation}
				title={t('CURRENCIES.DELETE_TITLE')}
				description={t('CURRENCIES.DELETE_DESCRIPTION')}
				note={t('CURRENCIES.DELETE_NOTE')}
				onConfirm={onDelete}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default ListCurrenciesPage;
